import type {
  SecretAnswerCommand,
  SecretAnswerProjectedState,
  SecretAnswerValue,
} from "@cue-the-night/game-secret-answer";

import type {
  HostedGameSnapshot,
  HostedPartySnapshot,
} from "@cue-the-night/transport";

import type {
  HostedPartyClient,
} from "./hosted-party-client";


export interface SecretAnswerPlayerRenderOptions {
  readonly panel:
    HTMLElement;

  readonly party:
    HostedPartySnapshot;

  readonly game:
    HostedGameSnapshot;

  readonly participantId:
    string;

  readonly client:
    HostedPartyClient;

  readonly onGameSnapshot:
    (
      snapshot:
        HostedGameSnapshot,
    ) => void;
}


export interface SecretAnswerOwnerRenderOptions {
  readonly panel:
    HTMLElement;

  readonly party:
    HostedPartySnapshot;

  readonly game:
    HostedGameSnapshot;
}


function isRecord(
  value:
    unknown,
): value is
Record<
  string,
  unknown
> {
  return (
    typeof value ===
      "object" &&
    value !==
      null &&
    !Array.isArray(
      value,
    )
  );
}


function escapeHtml(
  value:
    string,
): string {
  const element =
    document.createElement(
      "div",
    );

  element.textContent =
    value;

  return element.innerHTML;
}


function readState(
  game:
    HostedGameSnapshot,
): SecretAnswerProjectedState {
  if (
    game.gameId !==
    "secret-answer"
  ) {
    throw new Error(
      `Unsupported Secret Answer game: ${game.gameId}`,
    );
  }

  if (
    !isRecord(
      game.publicState,
    )
  ) {
    throw new Error(
      "Secret Answer public state is invalid.",
    );
  }

  return game.publicState as unknown as
    SecretAnswerProjectedState;
}


function playerName(
  party:
    HostedPartySnapshot,

  participantId:
    string,
): string {
  return (
    party.participants.find(
      (
        participant,
      ) =>
        participant
          .participantId ===
        participantId,
    )?.displayName ??
    "Player"
  );
}


function isRevealPhase(
  state:
    SecretAnswerProjectedState,
): boolean {
  return (
    state.phase ===
      "reveal" ||
    state.phase ===
      "outcome" ||
    state.phase ===
      "host_reaction" ||
    state.phase ===
      "complete"
  );
}


function card(
  badge:
    string,

  title:
    string,

  copy:
    string,
): string {
  return `
    <section
      style="
        display:grid;
        gap:6px;
        padding:16px;
        border-radius:18px;
        border:1px solid rgba(205,100,255,.30);
        background:rgba(9,7,15,.86);
      "
    >
      <span
        style="
          color:#da99ff;
          font-size:.7rem;
          font-weight:900;
          letter-spacing:.12em;
        "
      >
        ${escapeHtml(badge)}
      </span>

      <strong
        style="
          color:#fff;
          font-size:1rem;
        "
      >
        ${escapeHtml(title)}
      </strong>

      <span
        style="
          color:rgba(255,255,255,.62);
          line-height:1.45;
        "
      >
        ${escapeHtml(copy)}
      </span>
    </section>
  `;
}


function shell(
  title:
    string,

  copy:
    string,

  body:
    string,
): string {
  return `
    <div
      data-secret-answer-root
      style="
        display:grid;
        gap:16px;
        width:min(100%,760px);
        margin:0 auto;
        padding:20px;
        box-sizing:border-box;
      "
    >
      <header>
        <div
          style="
            color:#da99ff;
            font-size:.72rem;
            font-weight:900;
            letter-spacing:.14em;
          "
        >
          SECRET ANSWER
        </div>

        <h2
          style="
            color:#fff;
            font-size:2rem;
            line-height:1.08;
            margin:7px 0 0;
          "
        >
          ${escapeHtml(title)}
        </h2>

        <p
          style="
            color:rgba(255,255,255,.62);
            line-height:1.5;
            margin:9px 0 0;
          "
        >
          ${escapeHtml(copy)}
        </p>
      </header>

      ${body}

      <div
        data-sa-status
        style="
          color:rgba(255,255,255,.55);
          font-size:.82rem;
        "
      ></div>
    </div>
  `;
}


function promptCard(
  question:
    string,
): string {
  return `
    <section
      style="
        padding:18px;
        border-radius:20px;
        border:1px solid rgba(205,100,255,.38);
        background:rgba(20,8,27,.90);
      "
    >
      <div
        style="
          color:#da99ff;
          font-size:.7rem;
          font-weight:900;
          letter-spacing:.12em;
        "
      >
        THE QUESTION
      </div>

      <div
        style="
          color:#fff;
          font-size:1.22rem;
          font-weight:800;
          line-height:1.4;
          margin-top:9px;
        "
      >
        ${escapeHtml(question)}
      </div>
    </section>
  `;
}


function action(
  label:
    string,

  attributes:
    string,
): string {
  return `
    <button
      type="button"
      ${attributes}
      style="
        width:100%;
        min-height:54px;
        border-radius:16px;
        border:1px solid rgba(218,153,255,.52);
        background:rgba(145,45,205,.31);
        color:#fff;
        font-weight:900;
        padding:12px 14px;
        cursor:pointer;
      "
    >
      ${escapeHtml(label)}
    </button>
  `;
}


function specialCard(
  state:
    SecretAnswerProjectedState,
): string {
  const round =
    state.round;

  if (!round) {
    return "";
  }

  if (
    round.specialEvent ===
    "unfinished_business"
  ) {
    return card(
      "HERO · UNFINISHED BUSINESS",
      "The rematch is here.",
      "A real earlier Miss gets another shot.",
    );
  }

  if (
    round.roundType ===
    "all_in"
  ) {
    return card(
      "ALL IN",
      "Everybody predicts.",
      "Each Predictor confirms and locks independently on their own connected screen.",
    );
  }

  if (
    round.roundType ===
    "open_secret"
  ) {
    return card(
      "OPEN SECRET",
      "Say it your way.",
      "Free text stays private until reveal. The Answerer judges it manually.",
    );
  }

  if (
    round.roundType ===
    "then_vs_now"
  ) {
    return card(
      "THEN VS NOW",
      "Two moments. One answer.",
      "Choose what was true then and what is true now.",
    );
  }

  if (
    round.specialEvent ===
    "secret_bet"
  ) {
    return card(
      "SECRET BET",
      "Exact or nothing.",
      "An exact prediction scores 3. Anything else scores 0.",
    );
  }

  if (
    round.specialEvent ===
    "perfect_match"
  ) {
    return card(
      "PERFECT MATCH",
      "Only exact counts.",
      "Close does not score this special.",
    );
  }

  if (
    round.specialEvent ===
    "callback"
  ) {
    return card(
      "CALLBACK",
      "This feels familiar.",
      "Only the question family returns. Old private values never do.",
    );
  }

  return "";
}


function valueLabel(
  value:
    SecretAnswerValue | null,

  state:
    SecretAnswerProjectedState,
): string {
  if (!value) {
    return "—";
  }

  const prompt =
    state.round
      ?.prompt;

  if (
    value.kind ===
    "OPTION"
  ) {
    return (
      prompt
        ?.options
        ?.find(
          (
            option,
          ) =>
            option.id ===
            value.optionId,
        )
        ?.text ??
      value.optionId
    );
  }

  if (
    value.kind ===
    "NUMBER"
  ) {
    return (
      prompt
        ?.numericChoices
        ?.find(
          (
            choice,
          ) =>
            choice.value ===
            value.value,
        )
        ?.label ??
      String(
        value.value,
      )
    );
  }

  if (
    value.kind ===
    "TEXT"
  ) {
    return value.value;
  }

  const then =
    prompt
      ?.options
      ?.find(
        (
          option,
        ) =>
          option.id ===
          value.thenOptionId,
      )
      ?.text ??
    value.thenOptionId;

  const now =
    prompt
      ?.options
      ?.find(
        (
          option,
        ) =>
          option.id ===
          value.nowOptionId,
      )
      ?.text ??
    value.nowOptionId;

  return `Then: ${then} · Now: ${now}`;
}


function revealCards(
  state:
    SecretAnswerProjectedState,

  party:
    HostedPartySnapshot,

  showOutcome:
    boolean,
): string {
  const round =
    state.round;

  if (
    !round ||
    !isRevealPhase(
      state,
    )
  ) {
    return "";
  }

  const answerer =
    playerName(
      party,
      round.answererId,
    );

  const answer =
    card(
      `${answerer.toUpperCase()} · ANSWER`,
      valueLabel(
        round.answer,
        state,
      ),
      "Authoritative reveal",
    );

  const predictions =
    round.predictions
      .map(
        (
          prediction,
        ) => {
          const name =
            playerName(
              party,
              prediction
                .participantId,
            );

          const outcome =
            round.outcomes[
              prediction
                .participantId
            ];

          return card(
            `${name.toUpperCase()} · PREDICTION`,
            valueLabel(
              prediction.value,
              state,
            ),
            (
              showOutcome &&
              outcome
                ? `${
                    outcome.outcome ===
                    "nailed"
                      ? "NAILED IT"
                      : outcome.outcome ===
                        "close"
                        ? "SO CLOSE"
                        : "MISSED"
                  } · ${outcome.points} PTS`
                : "Revealed"
            ),
          );
        },
      )
      .join("");

  return `
    <div
      style="
        display:grid;
        gap:10px;
      "
    >
      ${answer}
      ${predictions}
    </div>
  `;
}


function valueControls(
  state:
    SecretAnswerProjectedState,

  mode:
    "answer" |
    "prediction",
): string {
  const round =
    state.round;

  if (!round) {
    return "";
  }

  const options =
    round.prompt.options ??
    [];

  const numeric =
    round.prompt
      .numericChoices ??
    [];

  if (
    round.prompt.format ===
    "open_secret"
  ) {
    return `
      <div
        style="
          display:grid;
          gap:10px;
        "
      >
        <textarea
          data-sa-text
          maxlength="80"
          rows="4"
          placeholder="${
            mode ===
            "answer"
              ? "Type your private answer…"
              : "Type your private prediction…"
          }"
          style="
            width:100%;
            box-sizing:border-box;
            resize:vertical;
            padding:14px;
            border-radius:16px;
            border:1px solid rgba(218,153,255,.40);
            background:rgba(8,7,14,.9);
            color:#fff;
            font:inherit;
          "
        ></textarea>

        ${
          action(
            mode ===
            "answer"
              ? "LOCK MY ANSWER"
              : "LOCK MY PREDICTION",
            `data-sa-text-lock="${mode}"`,
          )
        }
      </div>
    `;
  }

  if (
    round.prompt.format ===
    "then_vs_now"
  ) {
    const choices =
      options
        .map(
          (
            option,
          ) => `
            <option
              value="${escapeHtml(option.id)}"
            >
              ${escapeHtml(option.text)}
            </option>
          `,
        )
        .join("");

    return `
      <div
        style="
          display:grid;
          gap:12px;
        "
      >
        <label style="color:#fff;font-weight:800;">
          THEN
          <select
            data-sa-then
            style="
              display:block;
              width:100%;
              box-sizing:border-box;
              margin-top:6px;
              padding:11px;
            "
          >
            ${choices}
          </select>
        </label>

        <label style="color:#fff;font-weight:800;">
          NOW
          <select
            data-sa-now
            style="
              display:block;
              width:100%;
              box-sizing:border-box;
              margin-top:6px;
              padding:11px;
            "
          >
            ${choices}
          </select>
        </label>

        ${
          action(
            mode ===
            "answer"
              ? "LOCK THEN + NOW"
              : "LOCK PREDICTION",
            `data-sa-then-now="${mode}"`,
          )
        }
      </div>
    `;
  }

  const optionButtons =
    options
      .map(
        (
          option,
          index,
        ) =>
          action(
            `${String.fromCharCode(
              65 +
              index,
            )} · ${option.text}`,
            `data-sa-option="${escapeHtml(option.id)}" data-sa-mode="${mode}"`,
          ),
      );

  const numericButtons =
    numeric
      .map(
        (
          choice,
        ) =>
          action(
            choice.label,
            `data-sa-number="${choice.value}" data-sa-mode="${mode}"`,
          ),
      );

  if (
    optionButtons.length ===
      0 &&
    numericButtons.length ===
      0
  ) {
    return card(
      "PRIVATE",
      "Input unavailable",
      "No selectable private values were projected to this player.",
    );
  }

  return `
    <div
      style="
        display:grid;
        gap:9px;
      "
    >
      ${
        [
          ...optionButtons,
          ...numericButtons,
        ].join("")
      }
    </div>
  `;
}


function predictionCommand(
  state:
    SecretAnswerProjectedState,

  value:
    SecretAnswerValue,
): SecretAnswerCommand {
  return {
    type:
      "LOCK_PREDICTION",

    value,

    ...(
      state.round
        ?.specialEvent ===
      "secret_bet"
        ? {
            secretBet:
              true,
          }
        : {}
    ),
  };
}


async function send(
  options:
    SecretAnswerPlayerRenderOptions,

  command:
    SecretAnswerCommand,
): Promise<void> {
  const status =
    options.panel
      .querySelector<HTMLElement>(
        "[data-sa-status]",
      );

  if (status) {
    status.textContent =
      "Updating…";
  }

  try {
    const result =
      await options.client
        .sendGameCommand(
          options.game
            .gameSessionId,

          options.game
            .stateVersion,

          command as unknown as
            Readonly<
              Record<
                string,
                unknown
              >
            >,
        );

    options.onGameSnapshot(
      result.snapshot,
    );

    renderPlayerSecretAnswer({
      ...options,
      game:
        result.snapshot,
    });
  }
  catch (error) {
    if (status) {
      status.textContent =
        error instanceof
          Error
          ? error.message
          : "Unable to update Secret Answer.";
    }
  }
}


function bindControls(
  options:
    SecretAnswerPlayerRenderOptions,

  state:
    SecretAnswerProjectedState,
): void {
  const confirm =
    options.panel
      .querySelector<HTMLButtonElement>(
        "[data-sa-confirm]",
      );

  confirm
    ?.addEventListener(
      "click",
      () => {
        void send(
          options,
          {
            type:
              "CONFIRM_PREDICTOR",
          },
        );
      },
    );


  options.panel
    .querySelectorAll<HTMLButtonElement>(
      "[data-sa-option]",
    )
    .forEach(
      (
        button,
      ) => {
        button.addEventListener(
          "click",
          () => {
            const optionId =
              button.dataset
                .saOption;

            const mode =
              button.dataset
                .saMode;

            if (
              !optionId ||
              (
                mode !==
                "answer" &&
                mode !==
                "prediction"
              )
            ) {
              return;
            }

            const value:
              SecretAnswerValue = {
                kind:
                  "OPTION",

                optionId,
              };

            void send(
              options,
              mode ===
              "answer"
                ? {
                    type:
                      "LOCK_ANSWER",

                    value,
                  }
                : predictionCommand(
                    state,
                    value,
                  ),
            );
          },
        );
      },
    );


  options.panel
    .querySelectorAll<HTMLButtonElement>(
      "[data-sa-number]",
    )
    .forEach(
      (
        button,
      ) => {
        button.addEventListener(
          "click",
          () => {
            const numeric =
              Number(
                button.dataset
                  .saNumber,
              );

            const mode =
              button.dataset
                .saMode;

            if (
              !Number.isFinite(
                numeric,
              ) ||
              (
                mode !==
                "answer" &&
                mode !==
                "prediction"
              )
            ) {
              return;
            }

            const value:
              SecretAnswerValue = {
                kind:
                  "NUMBER",

                value:
                  numeric,
              };

            void send(
              options,
              mode ===
              "answer"
                ? {
                    type:
                      "LOCK_ANSWER",

                    value,
                  }
                : predictionCommand(
                    state,
                    value,
                  ),
            );
          },
        );
      },
    );


  const textButton =
    options.panel
      .querySelector<HTMLButtonElement>(
        "[data-sa-text-lock]",
      );

  textButton
    ?.addEventListener(
      "click",
      () => {
        const input =
          options.panel
            .querySelector<HTMLTextAreaElement>(
              "[data-sa-text]",
            );

        const mode =
          textButton.dataset
            .saTextLock;

        const text =
          input?.value
            .trim() ??
          "";

        if (
          text.length ===
            0 ||
          (
            mode !==
            "answer" &&
            mode !==
            "prediction"
          )
        ) {
          return;
        }

        const value:
          SecretAnswerValue = {
            kind:
              "TEXT",

            value:
              text,
        };

        void send(
          options,
          mode ===
          "answer"
            ? {
                type:
                  "LOCK_ANSWER",

                value,
              }
            : predictionCommand(
                state,
                value,
              ),
        );
      },
    );


  const thenNowButton =
    options.panel
      .querySelector<HTMLButtonElement>(
        "[data-sa-then-now]",
      );

  thenNowButton
    ?.addEventListener(
      "click",
      () => {
        const thenSelect =
          options.panel
            .querySelector<HTMLSelectElement>(
              "[data-sa-then]",
            );

        const nowSelect =
          options.panel
            .querySelector<HTMLSelectElement>(
              "[data-sa-now]",
            );

        const mode =
          thenNowButton.dataset
            .saThenNow;

        if (
          !thenSelect ||
          !nowSelect ||
          (
            mode !==
            "answer" &&
            mode !==
            "prediction"
          )
        ) {
          return;
        }

        const value:
          SecretAnswerValue = {
            kind:
              "THEN_NOW",

            thenOptionId:
              thenSelect.value,

            nowOptionId:
              nowSelect.value,
          };

        void send(
          options,
          mode ===
          "answer"
            ? {
                type:
                  "LOCK_ANSWER",

                value,
              }
            : predictionCommand(
                state,
                value,
              ),
        );
      },
    );


  options.panel
    .querySelectorAll<HTMLButtonElement>(
      "[data-sa-judge]",
    )
    .forEach(
      (
        button,
      ) => {
        button.addEventListener(
          "click",
          () => {
            const judgement =
              button.dataset
                .saJudge;

            if (
              judgement !==
                "match" &&
              judgement !==
                "close" &&
              judgement !==
                "nope"
            ) {
              return;
            }

            void send(
              options,
              {
                type:
                  "JUDGE_OPEN_SECRET",

                judgement,
              },
            );
          },
        );
      },
    );
}


export function renderPlayerSecretAnswer(
  options:
    SecretAnswerPlayerRenderOptions,
): void {
  const state =
    readState(
      options.game,
    );

  const round =
    state.round;

  if (!round) {
    options.panel.innerHTML =
      shell(
        "Getting the secret ready.",
        "Your private role will appear here.",
        card(
          "CONNECTED",
          "Private by design",
          "Waiting for the authoritative round.",
        ),
      );

    return;
  }

  const answererName =
    playerName(
      options.party,
      round.answererId,
    );

  const isAnswerer =
    options.participantId ===
    round.answererId;

  const isPredictor =
    round.predictorIds
      .some(
        (
          participantId,
        ) =>
          participantId ===
          options.participantId,
      );

  const isConfirmed =
    round.confirmedPredictorIds
      .some(
        (
          participantId,
        ) =>
          participantId ===
          options.participantId,
      );

  const ownPrediction =
    round.predictions
      .find(
        (
          prediction,
        ) =>
          prediction
            .participantId ===
          options.participantId,
      );

  let title =
    "Secret Answer";

  let copy =
    `Round ${state.roundNumber} of ${state.totalRounds}.`;

  let body =
    specialCard(
      state,
    );

  if (
    state.phase ===
      "SETUP" ||
    state.phase ===
      "intro"
  ) {
    title =
      "Wait for the Host.";

    copy =
      "The round has not opened yet.";

    body +=
      card(
        "CONNECTED",
        "No private input yet",
        "Your role appears when the Host begins the round.",
      );
  }
  else if (
    state.phase ===
    "answering"
  ) {
    body +=
      promptCard(
        round.prompt
          .question,
      );

    if (isAnswerer) {
      title =
        "Your secret.";

      copy =
        "Only your connected player view receives these answer controls.";

      body +=
        valueControls(
          state,
          "answer",
        );
    }
    else {
      title =
        `${answererName} is answering.`;

      copy =
        "Their private answer is hidden.";

      body +=
        card(
          "PLAYER VIEW",
          "Answer protected",
          "Only the Answerer receives answer controls.",
        );
    }
  }
  else if (
    state.phase ===
    "answer_locked"
  ) {
    title =
      "Secret secured.";

    copy =
      "The Host is opening predictions.";

    body +=
      card(
        "LOCKED",
        "No peeking",
        "The Answerer's value remains hidden.",
      );
  }
  else if (
    state.phase ===
      "handoff" ||
    state.phase ===
      "predicting"
  ) {
    body +=
      promptCard(
        round.prompt
          .question,
      );

    if (
      isPredictor &&
      !isConfirmed
    ) {
      title =
        "Your turn to predict.";

      copy =
        `What did ${answererName} choose?`;

      body +=
        card(
          "PRIVATE",
          "Confirm your screen",
          "Only your own Predictor controls will unlock.",
        );

      body +=
        action(
          "I'M READY",
          "data-sa-confirm",
        );
    }
    else if (
      isPredictor &&
      isConfirmed &&
      !ownPrediction
        ?.locked
    ) {
      title =
        `What did ${answererName} choose?`;

      copy =
        "Lock your prediction on this device.";

      body +=
        valueControls(
          state,
          "prediction",
        );
    }
    else if (
      isPredictor &&
      ownPrediction
        ?.locked
    ) {
      title =
        "Your call is in.";

      copy =
        round.roundType ===
          "all_in"
          ? "Other Predictors can finish independently."
          : "Wait for the reveal.";

      body +=
        card(
          "LOCKED",
          "Prediction secured",
          "Nobody else sees your value before reveal.",
        );
    }
    else {
      title =
        "Someone else is predicting.";

      copy =
        "This screen never impersonates another player.";

      body +=
        card(
          "PLAYER VIEW",
          "Prediction protected",
          "Wait for the required Predictor.",
        );
    }
  }
  else if (
    state.phase ===
    "waiting"
  ) {
    title =
      "Private input in progress.";

    copy =
      "The session is waiting for a required player lock.";

    body +=
      card(
        "WAITING",
        "Stay connected",
        "No other player's private value is shown here.",
      );
  }
  else if (
    state.phase ===
    "prediction_locked"
  ) {
    title =
      "Everything is locked.";

    copy =
      "The Host controls the reveal.";

    body +=
      card(
        "READY",
        "Secrets still hidden",
        "No answer or prediction is exposed before reveal.",
      );
  }
  else if (
    state.phase ===
    "reveal"
  ) {
    title =
      round.roundType ===
        "all_in"
        ? "Everybody made the call."
        : "Secret vs prediction.";

    copy =
      "The authoritative reveal is live.";

    body +=
      promptCard(
        round.prompt
          .question,
      );

    body +=
      revealCards(
        state,
        options.party,
        false,
      );

    if (
      round.roundType ===
      "open_secret"
    ) {
      if (isAnswerer) {
        body +=
          card(
            "ANSWERER",
            "You judge it",
            "No AI matching. Your judgement is authoritative.",
          );

        body += `
          <div
            style="
              display:grid;
              gap:9px;
            "
          >
            ${
              action(
                "MATCH · 2 PTS",
                'data-sa-judge="match"',
              )
            }

            ${
              action(
                "CLOSE · 1 PT",
                'data-sa-judge="close"',
              )
            }

            ${
              action(
                "NOPE · 0 PTS",
                'data-sa-judge="nope"',
              )
            }
          </div>
        `;
      }
      else {
        body +=
          card(
            "WAIT",
            "Waiting for the Answerer",
            "Only the Answerer judges Open Secret.",
          );
      }
    }
  }
  else if (
    state.phase ===
      "outcome" ||
    state.phase ===
      "host_reaction"
  ) {
    title =
      "The call is settled.";

    copy =
      "Scores come from the authoritative outcome.";

    body +=
      promptCard(
        round.prompt
          .question,
      );

    body +=
      revealCards(
        state,
        options.party,
        true,
      );
  }
  else if (
    state.phase ===
    "void"
  ) {
    title =
      "Round skipped.";

    copy =
      "Nothing private carries forward.";

    body +=
      card(
        "VOID",
        "No score change",
        "The skipped round does not seed secret history.",
      );
  }
  else if (
    state.phase ===
    "complete"
  ) {
    title =
      "Secrets survived.";

    copy =
      "Final score.";

    body +=
      options.party
        .participants
        .map(
          (
            participant,
          ) =>
            card(
              "FINAL",
              participant
                .displayName,
              `${
                state.scores[
                  participant
                    .participantId
                ] ??
                0
              } PTS`,
            ),
        )
        .join("");
  }

  options.panel.innerHTML =
    shell(
      title,
      copy,
      body,
    );

  bindControls(
    options,
    state,
  );
}


export function renderOwnerSecretAnswer(
  options:
    SecretAnswerOwnerRenderOptions,
): void {
  const state =
    readState(
      options.game,
    );

  const round =
    state.round;

  if (!round) {
    options.panel.innerHTML =
      shell(
        "Secret Answer is preparing.",
        "Owner Web remains a passive presentation.",
        card(
          "OWNER VIEW",
          "Privacy protected",
          "Private player controls never render here.",
        ),
      );

    return;
  }

  let body =
    specialCard(
      state,
    );

  body +=
    promptCard(
      round.prompt
        .question,
    );

  if (
    isRevealPhase(
      state,
    )
  ) {
    body +=
      revealCards(
        state,
        options.party,
        state.phase !==
          "reveal",
      );
  }
  else {
    const confirmed =
      round
        .confirmedPredictorIds
        .length;

    const locked =
      round.predictions
        .filter(
          (
            prediction,
          ) =>
            prediction.locked,
        )
        .length;

    body +=
      card(
        "OWNER VIEW",
        round.answerLocked
          ? "Answer locked"
          : "Answer open",
        `${confirmed}/${round.predictorIds.length} Predictors confirmed · ${locked}/${round.predictorIds.length} predictions locked.`,
      );

    body +=
      card(
        "PRIVACY",
        "No private values rendered",
        "Owner presentation is passive before reveal.",
      );
  }

  options.panel.innerHTML =
    shell(
      "Secret Answer is live.",
      "Round orchestration remains on the authoritative Owner controller.",
      body,
    );
}
